import React from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
import { CheckIcon, ArrowRightIcon, MessageCircleIcon, PenToolIcon, CodeIcon, CheckCircleIcon, ServerIcon, TestTubeIcon, RocketIcon, ShieldIcon, UsersIcon, TrendingUpIcon, FileTextIcon, ShoppingCartIcon, LayoutIcon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type WebsiteType = {
  icon: React.ReactNode;
  title: string;
  scope: string;
  price: string;
  priceSuffix: string;
  details: string;
  highlight?: boolean;
};
export const WebsitesPage: React.FC = () => {
  const websiteTypes: WebsiteType[] = [{
    icon: <FileTextIcon className="h-6 w-6" data-id="element-3260" data-mp="2pdwbum0cfafr" />,
    title: 'Onepager',
    scope: "1 tot 3 secties op één pagina",
    price: 'vanaf €499',
    priceSuffix: 'eenmalig, excl. BTW',
    details: 'Een strakke, conversiegerichte onepager met één duidelijke boodschap. Geschikt voor zzp en kleine dienstverleners die snel online willen staan met een professioneel visitekaartje.',
    highlight: true
  }, {
    icon: <LayoutIcon className="h-6 w-6" data-id="element-3261" data-mp="3hb51cd1ka05n" />,
    title: 'Maatwerk website',
    scope: "4 of meer pagina's",
    price: 'Op aanvraag',
    priceSuffix: 'afgestemd op scope',
    details: 'Een volwaardige website met meerdere dienst-paginas, een blog of kennisbank, en ruimte voor SEO-uitbreiding. Voor bedrijven die hun complete dienstverlening online willen presenteren.'
  }, {
    icon: <ShoppingCartIcon className="h-6 w-6" data-id="element-3262" data-mp="2yfy5l99x23py" />,
    title: 'Webshop',
    scope: 'E-commerce, WooCommerce of maatwerk',
    price: 'Op aanvraag',
    priceSuffix: 'incl. betaalkoppeling',
    details: 'Een complete webshop met betalingen, voorraadbeheer en ordermails. Voor bedrijven die producten of diensten direct via de site willen verkopen.'
  }];
  const benefits = [{
    icon: <CheckCircleIcon className="h-6 w-6" data-id="element-3263" data-mp="nlailzuvhy3j" />,
    title: 'Custom design voor jouw merk',
    description: 'Volledig op maat ontworpen, met aandacht voor je doelgroep en de manier waarop jij wilt overkomen.'
  }, {
    icon: <UsersIcon className="h-6 w-6" data-id="element-3264" data-mp="zmikvbra8hof" />,
    title: 'Specialist in zakelijke dienstverlening',
    description: 'Ervaring met websites voor financieel adviseurs, administratiekantoren, juristen en M&A specialisten.'
  }, {
    icon: <TrendingUpIcon className="h-6 w-6" data-id="element-3265" data-mp="bysm24ykt8i6" />,
    title: 'Gebruiksvriendelijk en helder',
    description: 'Intuitieve navigatie en een heldere structuur, zodat bezoekers snel vinden waar ze voor komen.'
  }, {
    icon: <ShieldIcon className="h-6 w-6" data-id="element-3266" data-mp="lx8ce5ps4dqm" />,
    title: 'Professionele uitstraling',
    description: 'Past bij sectoren waar vertrouwen alles is. Geen schreeuwerige opmaak, wel een verzorgde, premium look.'
  }, {
    icon: <StarIcon className="h-6 w-6" data-id="element-3267" data-mp="3pegtgncon9ka" />,
    title: 'Klaar voor SEO en groei',
    description: 'Vanaf dag één geschikt voor SEO-optimalisatie en uitbreiding wanneer je markt erom vraagt.'
  }];
  const topCases = [{
    name: 'Alex Construct',
    sector: 'Renovatie',
    image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800&h=500&fit=crop'
  }, {
    name: 'Mijn Bedrijfs Waarderingen',
    sector: 'Financiële Dienstverlening',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=500&fit=crop'
  }, {
    name: 'GymFit de Noord',
    sector: 'Crossfit',
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&h=500&fit=crop'
  }];
  const timelineSteps = [{
    icon: <MessageCircleIcon className="h-6 w-6" data-id="element-3268" data-mp="1nqrvqcnbn160" />,
    title: 'Intake',
    description: 'We brengen je doelen, doelgroep en wensen helder in kaart via een persoonlijk gesprek.'
  }, {
    icon: <PenToolIcon className="h-6 w-6" data-id="element-3269" data-mp="1esy2jakusnn2" />,
    title: 'Ontwerp',
    description: 'We maken een eerste ontwerp dat past bij jouw organisatie en je sector.'
  }, {
    icon: <UsersIcon className="h-6 w-6" data-id="element-3270" data-mp="14sp4dr7aws51" />,
    title: 'Afstemmen',
    description: 'Je geeft feedback, wij verfijnen. Dit herhalen we tot het echt goed voelt.'
  }, {
    icon: <CodeIcon className="h-6 w-6" data-id="element-3271" data-mp="34xsyt6d92fhe" />,
    title: 'Bouwen',
    description: 'Is het ontwerp akkoord? Dan bouwen we de website technisch op, snel, veilig en schaalbaar.'
  }, {
    icon: <TestTubeIcon className="h-6 w-6" data-id="element-3272" data-mp="2v5dhmb9s1uxk" />,
    title: 'Testen en finetunen',
    description: 'Je bekijkt de website en geeft feedback. Wij passen aan tot alles klopt.'
  }, {
    icon: <RocketIcon className="h-6 w-6" data-id="element-3273" data-mp="2m3dwp7vn66pc" />,
    title: 'Oplevering',
    description: 'Bij oplevering geven we live uitleg over hoe je met je website en systemen werkt.'
  }, {
    icon: <ServerIcon className="h-6 w-6" data-id="element-3274" data-mp="2c7kk5ohxad7k" />,
    title: 'Hosting en onderhoud',
    description: 'Na oplevering zorgen wij voor hosting, onderhoud en beveiliging, op onze eigen omgeving.'
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-3275" data-mp="af67b3u8jyy0">
      <SEO title="Website laten maken vanaf €499 | Noord Digital" description="Onepager vanaf €499, maatwerk en webshops op aanvraag. Professionele websites voor zakelijke dienstverleners, met hosting, e-mail en SEO onder één dak." keywords="website laten maken, onepager 499, maatwerk website, webshop laten maken, zakelijke website, professionele website, custom webdesign" canonical="https://noorddigital.nl/websites" jsonLd={[buildServiceSchema({
      name: 'Websites',
      description: 'Professionele websites voor ondernemers in Noord-Holland Noord, gebouwd om gevonden te worden en vertrouwen te wekken.',
      path: '/websites',
      serviceType: 'Webdesign en websiteontwikkeling'
    }), buildBreadcrumbList([{
      name: 'Websites',
      path: '/websites'
    }])]} data-id="element-3276" data-mp="fe92zxobsstb" />
      <Header data-id="element-3277" data-mp="2siutdjrxni0s" />

      {/* Hero */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20 relative overflow-hidden" data-id="element-3278" data-mp="2nlur7jy7ezey">
        <div className="absolute inset-0 opacity-20" data-id="element-3279" data-mp="y7zdrxibz2oi">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-3280" data-mp="t8cyzjmutedl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-3281" data-mp="2y9flawdcb1v4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-3282" data-mp="32b0yyjxp7k6d">
            <div className="lg:col-span-7" data-id="element-3283" data-mp="32d5c7shhlvjb">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-3284" data-mp="37aduru6js1l3">
                Website laten maken, vanaf €499 voor een onepager
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-3285" data-mp="3q0v9uvygiynz">
                Van een strakke onepager tot een volwaardige maatwerk website of webshop. We bouwen voor zakelijke dienstverleners die online net zo overtuigend willen overkomen als in een persoonlijk gesprek. Inclusief hosting, e-mail en SEO als je dat wilt. Vanuit Oudkarspel werken we voor ondernemers in{' '}
                <a href="/werkgebied/alkmaar" className="underline underline-offset-4 decoration-white/50 hover:decoration-white transition-colors duration-200" data-id="element-3286" data-mp="3w4rqmg7sz230">Alkmaar</a>{' '}
                en de rest van Noord-Holland Noord.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-3287" data-mp="mxiafld9uhfq">
                <motion.a href="#keuze" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg group" data-id="element-3288" data-mp="1anutnsdyw0lm">
                  Bekijk de opties
                  <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3289" data-mp="1kdp9y1qur7hi" />
                </motion.a>
              </motion.div>
            </div>

            <div className="lg:col-span-5" data-id="element-3290" data-mp="1pa7htahhbly2">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white rounded-2xl p-8 shadow-2xl relative" data-id="element-3291" data-mp="2d363x3u4ewfu">
                <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 4,
                ease: 'easeInOut'
              }} className="absolute -top-3 -right-3 w-16 h-16 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center shadow-lg border-4 border-white" data-id="element-3292" data-mp="281gq5gfik4o2">
                  <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte begeleidt je website" className="w-full h-full object-cover rounded-full" data-id="element-3293" data-mp="35vre6e2dam5s" />
                </motion.div>
                <div className="text-center mb-6" data-id="element-3294" data-mp="3fsw66uiw9bui">
                  <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-2" data-id="element-3295" data-mp="2s5xqw06ig9y6">
                    Onepager vanaf
                  </p>
                  <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-3296" data-mp="2n48f2ibgrs91">
                    <span className="text-6xl md:text-7xl font-bold text-[#111827]" data-id="element-3297" data-mp="30yrc0e8n23jy">
                      €499
                    </span>
                  </div>
                  <p className="text-[#6B7280] text-sm" data-id="element-3298" data-mp="28g3s325doukg">eenmalig, excl. BTW</p>
                </div>

                <div className="space-y-3 pt-6 border-t border-gray-200 mb-6" data-id="element-3299" data-mp="24eilfyjftcip">
                  <div className="flex items-center gap-3" data-id="element-3300" data-mp="22uhrot485z7f">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-3301" data-mp="22rvk4thc3jp7" />
                    <span className="text-sm text-[#6B7280]" data-id="element-3302" data-mp="1xvoc8v5dny4m">
                      Custom design op jouw merk
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3303" data-mp="1ouzy80fzhxrq">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-3304" data-mp="1owjj64cl4wzu" />
                    <span className="text-sm text-[#6B7280]" data-id="element-3305" data-mp="1jx6rn6arr1qx">
                      Contactformulier en CTA gerichte opbouw
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3306" data-mp="1a5u1cj4mcjn0">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-3307" data-mp="1648t5b314tj5" />
                    <span className="text-sm text-[#6B7280]" data-id="element-3308" data-mp="114cnz8iliw20">
                      Klaar voor SEO en uitbreiding
                    </span>
                  </div>
                </div>

                <a href="/prijsscan" className="block w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg text-center shadow-md" data-id="element-3309" data-mp="37sv76n3mrb6f">
                  <span className="flex items-center justify-center" data-id="element-3310" data-mp="3cqwr1a8vreiy">
                    Start de website scan
                    <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-3311" data-mp="3hrkejlxwhd8f" />
                  </span>
                </a>
                <p className="text-xs text-[#6B7280] text-center mt-4" data-id="element-3312" data-mp="3ls4dmv21aqx7">
                  Duurt 2 minuten, helemaal vrijblijvend
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Website types met prijs */}
      <div id="keuze" className="w-full bg-white py-20 md:py-24" data-id="element-3313" data-mp="2y2tknzqxe0tm">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3314" data-mp="32ts0ky804wwc">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3315" data-mp="32vwapxwhus52">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3316" data-mp="26b47oh0ew4x6">
              Kies het type website dat bij je past
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3317" data-mp="3ne7yc01k09rb">
              Van een compacte onepager tot een uitgebreide maatwerk website. Drie startpunten, allemaal met dezelfde aandacht voor design en techniek.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3318" data-mp="2zon2who7rk15">
            {websiteTypes.map((type, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5,
            borderColor: '#007AFF'
          }} className={`relative rounded-2xl p-8 transition-all duration-300 border-2 group ${type.highlight ? 'bg-white border-[#007AFF] shadow-lg' : 'bg-[#F7F8FA] border-transparent hover:shadow-lg'}`} data-id="element-3319" data-mp="izzoupx2sicj" data-mpi={index}>
                {type.highlight && <div className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#007AFF] text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full shadow-md" data-id="element-3320" data-mp="hj2nh519pv64">
                    <StarIcon className="h-3 w-3" data-id="element-3321" data-mp="1a6yh20vjj6yo" />
                    Populair
                  </div>}
                <motion.div whileHover={{
              scale: 1.1,
              rotate: 5
            }} className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-3322" data-mp="vibupf7hxexy">
                  {type.icon}
                </motion.div>
                <h3 className="font-bold text-[#111827] text-xl mb-1" data-id="element-3323" data-mp="1syrd645wp8am">
                  {type.title}
                </h3>
                <p className="text-[#007AFF] font-semibold text-sm mb-4" data-id="element-3324" data-mp="57l69ds544mh">
                  {type.scope}
                </p>
                <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-3325" data-mp="3rgw91l6jl6x9">
                  <p className="text-3xl font-bold text-[#111827] mb-1" data-id="element-3326" data-mp="3rh4jz7j3u25t">
                    {type.price}
                  </p>
                  <p className="text-xs text-[#6B7280]" data-id="element-3327" data-mp="o05u2gszfjjf">{type.priceSuffix}</p>
                </div>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-6" data-id="element-3328" data-mp="j12ngrxct59c">
                  {type.details}
                </p>
                <a href="/#contact" className={`inline-flex items-center justify-center w-full font-semibold py-3 px-4 rounded-lg text-sm transition ${type.highlight ? 'bg-[#007AFF] text-white shadow-md' : 'bg-white text-[#007AFF] border border-[#007AFF]'}`} data-id="element-3329" data-mp="e9kno772hapy">
                  Vraag dit type aan
                  <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-3330" data-mp="11zopqa4lx2eu" />
                </a>
              </motion.div>)}
          </div>

          <div className="max-w-3xl mx-auto mt-10 bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-6 text-center" data-id="element-3331" data-mp="1a9w3boyh6039">
            <p className="text-[#6B7280] leading-relaxed" data-id="element-3332" data-mp="1ec919r6zctph">
              Twijfel je tussen een onepager en een volwaardige website? Doe de website scan, dan kijken we samen welk type bij jouw doelen past.
            </p>
          </div>
        </div>
      </div>

      {/* Werkwijze timeline */}
      <div id="werkwijze" className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-3333" data-mp="1t5citykkn7g7">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3334" data-mp="1x6nz3dhk2gbq">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3335" data-mp="220ird5b883mf">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3336" data-mp="26z3k6jo5fl0u">
              Onze werkwijze: duidelijk, persoonlijk en zonder haast
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3337" data-mp="1m9sv9aa7yxhq">
              We werken stap voor stap. Ontwerp en afstemmen herhalen we net zo vaak tot je volledig tevreden bent.
            </motion.p>
          </div>

          <div className="relative max-w-4xl mx-auto" data-id="element-3338" data-mp="24zr07hiq0ah5">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#007AFF] bg-opacity-20 hidden md:block" data-id="element-3339" data-mp="2jy2z7utkfvbh">
              <motion.div initial={{
              scaleY: 0
            }} whileInView={{
              scaleY: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 1.5,
              ease: 'easeInOut'
            }} className="w-full bg-[#007AFF] origin-top" style={{
              height: '100%'
            }} data-id="element-3340" data-mp="2ezrozizczisq" />
            </div>

            <div className="space-y-12" data-id="element-3341" data-mp="315u2rmfuj2v7">
              {timelineSteps.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15
            }} className="relative flex items-center gap-8 md:gap-12" data-id="element-3342" data-mp="2mihfntae4p2p" data-mpi={index}>
                  <motion.div whileHover={{
                scale: 1.2
              }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[#007AFF] rounded-full items-center justify-center text-white shadow-lg hidden md:flex z-10" data-id="element-3343" data-mp="2hgs1i4rvfvae">
                    {step.icon}
                  </motion.div>
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:ml-auto md:text-left' : 'md:mr-auto md:text-right'}`} data-id="element-3344" data-mp="6ql6m872vv9r">
                    <motion.div whileHover={{
                  y: -5,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                }} className="bg-white rounded-xl p-6 shadow-md transition-all duration-300" data-id="element-3345" data-mp="2pobe7xw6y2n9">
                      <div className="flex items-center gap-3 mb-3 md:hidden" data-id="element-3346" data-mp="2gv7zzndq1lrk">
                        <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center text-white flex-shrink-0" data-id="element-3347" data-mp="2bx6hb3k7jogm">
                          {step.icon}
                        </div>
                        <h3 className="font-bold text-[#111827] text-lg" data-id="element-3348" data-mp="21y8ovpjnvasc">
                          {step.title}
                        </h3>
                      </div>
                      <h3 className="font-bold text-[#111827] text-lg mb-2 hidden md:block" data-id="element-3349" data-mp="1szvnvzl9bp93">
                        {step.title}
                      </h3>
                      <p className="text-[#6B7280] leading-relaxed" data-id="element-3350" data-mp="3s9vfek964gfe">
                        {step.description}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>)}
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5
        }} className="text-center mt-12" data-id="element-3351" data-mp="3pb7s708byvm">
            <motion.a href="/hosting" whileHover={{
            x: 5
          }} className="inline-flex items-center text-[#007AFF] font-medium" data-id="element-3352" data-mp="3by1nv75lil7m">
              Bekijk onze hosting (€34,99 per maand)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-3353" data-mp="3lw7m9es7197q" />
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Waarom maatwerk */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-3354" data-mp="3pyvzdynxx2ai">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3355" data-mp="116i9d7b1vxho">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3356" data-mp="15725q8nsux2r">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3357" data-mp="155iskqxfx257">
              Waarom een maatwerk website?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3358" data-mp="1jw6473j176nl">
              Een maatwerk website biedt de flexibiliteit en professionaliteit die jouw bedrijf nodig heeft. Geen template, wel een verhaal dat past bij jouw markt.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-3359" data-mp="27mjqedexqba9">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
          }} className="bg-[#F7F8FA] rounded-2xl p-6 transition-all duration-300 group" data-id="element-3360" data-mp="2cjtk459cx93w" data-mpi={index}>
                <motion.div whileHover={{
              scale: 1.1,
              rotate: 5
            }} className="w-12 h-12 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-3361" data-mp="3ececdlu7mxiy">
                  {benefit.icon}
                </motion.div>
                <h3 className="font-bold text-[#111827] text-lg mb-2" data-id="element-3362" data-mp="2zfev169k3qba">
                  {benefit.title}
                </h3>
                <p className="text-[#6B7280] leading-relaxed text-sm" data-id="element-3363" data-mp="12nmz7kgh375o">
                  {benefit.description}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 max-w-3xl mx-auto" data-id="element-3364" data-mp="2s48ujufqjjsr">
            <h3 className="font-bold text-[#111827] text-xl mb-4 text-center" data-id="element-3365" data-mp="2i4883zmkknkc">
              Compleet pakket bij één partij
            </h3>
            <p className="text-[#6B7280] text-center mb-6" data-id="element-3366" data-mp="3augpvqd715ye">
              Een professionele website is meer dan alleen design. Combineer met hosting, e-mail en SEO.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-3367" data-mp="31uld5h3zp2w9">
              <motion.a href="/hosting" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-3368" data-mp="31w4xbisrny3b">
                Hosting voor €34,99 per maand
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3369" data-mp="1ue5lhz1fc9t4" />
              </motion.a>
              <motion.a href="/email" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-3370" data-mp="1hyeavfwsquwh">
                Zakelijke e-mail vanaf €1,99
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3371" data-mp="145omj4tspdw7" />
              </motion.a>
              <motion.a href="/seo" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-3372" data-mp="z8y6ghhzqias">
                SEO optimalisatie
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3373" data-mp="leoz481y8c51" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Norden begeleiding */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-20 md:py-24" data-id="element-3374" data-mp="3fsth1gw5qkn9">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3375" data-mp="3jueylfjmzn3s">
          <div className="max-w-4xl mx-auto" data-id="element-3376" data-mp="3ou1ddtf45bep">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl" data-id="element-3377" data-mp="3tm16p30oh62b">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-3378" data-mp="3todys905gwt3">
                <div className="text-center md:text-left" data-id="element-3379" data-mp="2fqbhnicmziw">
                  <div className="inline-block relative" data-id="element-3380" data-mp="7ebe1tl2pvai">
                    <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} whileInView={{
                    opacity: 1,
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    type: 'spring',
                    stiffness: 100
                  }} className="w-48 h-48 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center p-4 relative overflow-hidden" data-id="element-3381" data-mp="7gxj9mkhrypk">
                      <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden begeleidt jouw website traject" className="w-full h-full object-cover" data-id="element-3382" data-mp="25wmvctvg8enz" />
                    </motion.div>
                    <motion.div animate={{
                    y: [0, -5, 0],
                    rotate: [0, 5, 0]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: 0
                  }} className="absolute -top-2 -right-2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-3383" data-mp="1w0jwbwvw8w59">
                      <CheckCircleIcon className="h-6 w-6 text-[#007AFF]" data-id="element-3384" data-mp="2fly4uhm8zlce" />
                    </motion.div>
                    <motion.div animate={{
                    y: [0, -5, 0],
                    rotate: [0, -5, 0]
                  }} transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: 1
                  }} className="absolute -bottom-2 -left-2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-3385" data-mp="18apgr7k6wthb">
                      <UsersIcon className="h-6 w-6 text-[#007AFF]" data-id="element-3386" data-mp="vpqzu8no6z8x" />
                    </motion.div>
                  </div>
                </div>
                <div data-id="element-3387" data-mp="my8k1yumylgd">
                  <motion.h2 initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} className="font-bold text-[#111827] text-3xl mb-4" data-id="element-3388" data-mp="mxp2ij8wdz2v">
                    Norden begeleidt jouw traject
                  </motion.h2>
                  <motion.p initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.1
                }} className="text-[#6B7280] text-lg leading-relaxed mb-6" data-id="element-3389" data-mp="3yuk90a86ghy">
                    Onze mascotte Norden staat symbool voor hoe wij werken. Als team, samen met jou, naar het gewenste resultaat. Van eerste gesprek tot oplevering staan wij naast je.
                  </motion.p>
                  <div className="space-y-3" data-id="element-3390" data-mp="8b9uk9fca14a">
                    <div className="flex items-center gap-3" data-id="element-3391" data-mp="89you42c2jaj">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3392" data-mp="3cyjxwh7zml0">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-3393" data-mp="h2sacj8diewu" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-3394" data-mp="d3b2xk9dz7p1">
                        Persoonlijke begeleiding door het hele traject
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-3395" data-mp="m3g77lja9pyz">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3396" data-mp="m13h2l3dg9au">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-3397" data-mp="1xgp9u112862a" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-3398" data-mp="22gbli618pe8z">
                        Directe communicatie met het ontwikkelteam
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-3399" data-mp="27d29rgn77g5h">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3400" data-mp="19spiayvxcbmn">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-3401" data-mp="1eqr07ghm64wj" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-3402" data-mp="1jo9cnguub29a">
                        Doorwerken tot je volledig tevreden bent
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cases */}
      <div className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3403" data-mp="vxwuze9slr5i">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3404" data-mp="offg0sj5dk55">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3405" data-mp="jh49aqo5fpr5">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-3406" data-mp="jfjgp6leagvx">
              Recent opgeleverd
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3407" data-mp="qcsx55quz39">
              Een paar van de laatste websites die we hebben opgeleverd. Bekijk de complete portfolio voor meer voorbeelden.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-3408" data-mp="3dzscqlkcn23g">
            {topCases.map((clientCase, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -10
          }} className="bg-white rounded-2xl overflow-hidden shadow-md transition-all duration-300 group" data-id="element-3409" data-mp="18ywslnsa7sce" data-mpi={index}>
                <div className="relative h-48 overflow-hidden" data-id="element-3410" data-mp="q8ykj7ohhz8r">
                  <motion.img whileHover={{
                scale: 1.1
              }} transition={{
                duration: 0.5
              }} src={clientCase.image} alt={`${clientCase.name} website door Noord Digital`} className="w-full h-full object-cover" data-id="element-3411" data-mp="q6ltm50r0ejk" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-3412" data-mp="bhzgm9ylfyvi"></div>
                  <div className="absolute bottom-4 left-4 right-4" data-id="element-3413" data-mp="7hfib32aap7s">
                    <span className="inline-block bg-[#007AFF] text-white text-xs font-medium px-3 py-1 rounded-full mb-2" data-id="element-3414" data-mp="3tj4ojsqws48g">
                      {clientCase.sector}
                    </span>
                    <h3 className="text-white font-bold text-lg" data-id="element-3415" data-mp="3jm01zrt5i6r4">
                      {clientCase.name}
                    </h3>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="text-center" data-id="element-3416" data-mp="g5jgwm3rbao9">
            <motion.a href="/#client-cases" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-flex items-center bg-[#007AFF] text-white font-semibold py-3 px-8 rounded-lg shadow-md group" data-id="element-3417" data-mp="b55a17dw66rt">
              Bekijk alle cases
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3418" data-mp="12cxdnwm9g92f" />
            </motion.a>
          </motion.div>
        </div>
      </div>

      <Footer data-id="element-3419" data-mp="xem29hgbhqi3" />
    </div>;
};